import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Homepage from "./homepage";
import Compare from "./compare";
import { PageWrapper, PageMain } from "./components/styles/common.styled";
import "./App.css";
import "./enrollment.css";
import "./section.css";
import Enrollment from "./components/enrollment/enrollment";
import IframeTestPage from "./components/IframeTestPage";
import NotFound from "./components/NotFound";
import EnrollmentV2 from "./components/v2/enrollment/enrollment";
import { AC_OFFER_CODES, DM_CODES } from "./components/data";
import AcPage from "./components/ac-pages/ac-page";
import DmPage from "./components/ac-pages/dm-page";
import React, { useEffect, useLayoutEffect } from "react";
import ConsteNew from "./components/consteNew";
import { PlanProvider } from "./components/consteNew/context/planContext";
import { ZipCodeProvider } from "./components/consteNew/ZipCodeContext";
import HeaderNew from "./components/consteNew/Header";
import FooterNew from "./components/consteNew/Footer";
import HomepageV2 from "./components/landingpagev2/HomepageV2";
import SmartServiceTermsPage from "./components/consteNew/SmartServiceTermsPage";
import { buildSearchWithStoredUtm, getStoredUtmParams, persistUtmParams } from "./utils/utmParams";


declare module "@mui/material/Button/Button" {
  interface ButtonPropsVariantOverrides {
    secondary: true;
  }
}

const globalTheme = createTheme({
  palette: {
    primary: {
      main: "#2372B9",
      dark: "#12395D",
      light: "#2372B9",
    },
    secondary: {
      main: "#f47b27",
      dark: "#e24319",
      light: "#f47b27",
    },
  },
});

const theme = createTheme(
  {
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: {
            backgroundColor: "#fff",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontWeight: 700,
            boxShadow: "none",
            textTransform: "inherit",
            "&:hover": {
              boxShadow: "none",
            },
          },
          sizeLarge: {
            fontSize: 16,
            padding: "16px 40px",
          },
          sizeMedium: {
            padding: "12px 20px",
          },
        },
        variants: [
          {
            props: { variant: "contained" },
            style: {
              color: "fff",
              "&:hover": {
                backgroundColor: globalTheme.palette.primary.dark,
              },
            },
          },
          {
            props: { variant: "outlined" },

            style: {
              boxShadow: "none",
              backgroundColor: "#fff",
              border: `1px solid #2372B9`,
              color: "#2372B9",
              // '&:hover': {
              // 	backgroundColor: '#2372B9',
              // 	borderColor: '#2372B9',
              // 	color: '#fff'
              // }
            },
          },
          {
            props: { variant: "secondary" },
            style: {
              backgroundColor: globalTheme.palette.secondary.main,
              color: "#fff",
              "&:hover": {
                backgroundColor: globalTheme.palette.secondary.dark,
              },
            },
          },
          {
            props: { variant: "text" },
            style: {
              backgroundColor: "transparent",
              boxShadow: "none",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
          },
        ],
      },
    },
  },
  globalTheme
);
declare global {
  interface Window {
    digitalData: {
      page: { pageInfo: { pageName: string } };
      ip: string;
    };
    dataLayer: any[];
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function PersistUtmParams() {
  const location = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    persistUtmParams(location.search);
    const stored = getStoredUtmParams();
    const hasMissingUtm = Object.entries(stored).some(
      ([key, value]) => value && !new URLSearchParams(location.search).get(key),
    );
    const nextSearch = hasMissingUtm
      ? buildSearchWithStoredUtm(location.search, stored)
      : location.search.startsWith("?")
        ? location.search.slice(1)
        : location.search;

    if (location.pathname === "/v2/enrollment") {
      navigate(
        { pathname: "/v2/enrollment/", search: nextSearch, hash: location.hash },
        { replace: true },
      );
      return;
    }

    if (!hasMissingUtm) return;

    navigate(
      { pathname: location.pathname, search: nextSearch, hash: location.hash },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  return null;
}

function App() {
  const [currentPath, setCurrentPath] = React.useState<string>("");
  const RedirectOfferCodes = () => {
    const { OFFERCODE } = useParams();
    const offerCodeUpper = OFFERCODE?.toUpperCase();

    if (
      !AC_OFFER_CODES.includes(offerCodeUpper as string) &&
      !DM_CODES.includes(offerCodeUpper as string)) {
      return <NotFound />;
    }

    return <Navigate to={"/" + OFFERCODE?.toUpperCase()} replace />;
  };

  useEffect(() => {
    const path = window.location.pathname;
    setCurrentPath(path);
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <ZipCodeProvider>
        <PlanProvider>
          <PageWrapper>
            <BrowserRouter>
              <ScrollToTop />
              <PersistUtmParams />
              <PageMain>
                <HeaderNew />
                <Routes>
                  {/* <Route path="/" element={<Homepage />} /> */}
                  <Route path="/" element={<HomepageV2 />} />
                  <Route path="/compare" element={<Compare />} />
                  <Route path="/state/tx/save" element={<ConsteNew />} />
                  <Route path="/smart-service-terms" element={<SmartServiceTermsPage />} />
                  <Route path="/enrollment" element={<Enrollment />} />
                  <Route path="/enrollment/:status" element={<Enrollment />} />
                  <Route path="/Iframe" element={<IframeTestPage />} />
                  {/* AC Protection Bundle Marketing Urls */}
                  {AC_OFFER_CODES.map((offercode) => (
                    <Route
                      key={offercode}
                      caseSensitive
                      path={`/${offercode}`}
                      element={<AcPage />}
                    />
                  ))}
                  {DM_CODES.map((offercode) => (
                    <Route
                      key={offercode}
                      caseSensitive
                      path={`/${offercode}`}
                      element={<DmPage />}
                    />
                  ))}
                  {/* Add new urls only above these */}
                  <Route path="/v2/enrollment" element={<EnrollmentV2 />} />
                  <Route path="/v2/enrollment/" element={<EnrollmentV2 />} />
                  <Route path="/v2/enrollment/:status" element={<EnrollmentV2 />} />
                  <Route path="/:OFFERCODE" element={<RedirectOfferCodes />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>                
              </PageMain>
              <FooterNew/>              
            </BrowserRouter>
          </PageWrapper>
        </PlanProvider>
      </ZipCodeProvider>
    </ThemeProvider>
  );
}

export default App;
